/**
 * Booking Service
 * Frontend service for Tourvisio Booking API integration
 */

// ==========================================
// TYPES
// ==========================================

export interface BookingSession {
    sessionId: string;
    transactionId: string;
    status: 'started' | 'passengers_set' | 'confirmed';
    expiresAt?: string;
    timeRemainingMs?: number;
    reservationData?: any;
}

export interface Passenger {
    id?: number;
    type: 'Adult' | 'Child' | 'Infant';
    title?: 'Mr' | 'Mrs' | 'Ms' | 'Miss';
    firstName: string;
    lastName: string;
    birthDate: string; // YYYY-MM-DD
    gender: 'Male' | 'Female';
    nationality: string; // 2-letter country code
    passportNumber?: string;
    passportExpiry?: string;
}

export interface Contact {
    firstName?: string;
    lastName?: string;
    email: string;
    phone: string;
    address?: string;
    city?: string;
    zipCode?: string;
    country?: string;
    countryCode?: string; // 2-letter country code
}

export interface ServiceOffer {
    offerId: string;
    travellers?: string[]; // Array of traveller IDs as strings
}

export interface PaymentOption {
    id: string;
    name: string;
    type: string;
    description?: string;
}

export interface BookingConfirmation {
    success: boolean;
    reservationNumber: string;
    bookingId?: string;
    message: string;
}

export interface TransactionDetails {
    sessionId: string;
    transactionId: string;
    status: string;
    offerIds: string[];
    additionalOffers: string[];
    currency: string;
    passengers: Passenger[] | null;
    contact: Contact | null;
    createdAt: string;
    expiresAt: string;
    timeRemainingMs: number;
    isExpired: boolean;
}

// ==========================================
// API CALLS
// ==========================================

const API_BASE = '/api/booking-flow';

/**
 * Start a booking transaction with selected offer(s)
 */
export async function startBooking(
    offerIds: string[],
    currency: string = 'EUR',
    flightData?: any,
    culture: string = 'en-US'
): Promise<BookingSession> {
    const response = await fetch(`${API_BASE}/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ offerIds, currency, flightData, culture })
    });

    const data = await response.json();

    if (!data.success) {
        throw new Error(data.error || 'Failed to start booking');
    }

    return {
        sessionId: data.sessionId,
        transactionId: data.transactionId,
        status: 'started',
        reservationData: data.reservationData || null
    };
}

interface BrandedFareFeature {
    name: string;
    included: boolean;
    icon: string;
    code?: string;
}

interface BrandedFareOption {
    id: string;
    offerId: string;
    name: string;
    features: BrandedFareFeature[];
    price: number;
    currency: string;
    isDefault: boolean;
    order: number;
}

export interface OfferDetailsResponse {
    success: boolean;
    offerId: string;
    outboundFlight?: any;
    returnFlight?: any;
    outboundBrandedFares: BrandedFareOption[];
    returnBrandedFares: BrandedFareOption[];
}

/**
 * Get offer details including branded fares
 */
export async function getOfferDetails(
    offerId: string,
    currency: string = 'SEK',
    culture: string = 'en-US'
): Promise<OfferDetailsResponse> {
    const response = await fetch(
        `${API_BASE}/offer-details/${encodeURIComponent(offerId)}?currency=${currency}&culture=${culture}`
    );

    const data = await response.json();

    return {
        success: data.success ?? false,
        offerId: data.offerId || offerId,
        outboundFlight: data.outboundFlight || null,
        returnFlight: data.returnFlight || null,
        outboundBrandedFares: data.outboundBrandedFares || [],
        returnBrandedFares: data.returnBrandedFares || []
    };
}

/**
 * Add extra services (transfer, extras) to an active transaction
 */
export async function addServices(
    sessionId: string,
    offers: ServiceOffer[],
    currency: string = 'EUR'
): Promise<{ success: boolean; reservationData?: unknown }> {
    const response = await fetch(`${API_BASE}/add-services`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId, offers, currency })
    });

    const data = await response.json();

    if (!data.success) {
        throw new Error(data.error || 'Failed to add services');
    }

    return data;
}

/**
 * Remove services from an active transaction
 */
export async function removeServices(
    sessionId: string,
    serviceIds: string[]
): Promise<{ success: boolean; reservationData?: unknown }> {
    const response = await fetch(`${API_BASE}/remove-services`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId, serviceIds })
    });

    const data = await response.json();

    if (!data.success) {
        throw new Error(data.error || 'Failed to remove services');
    }

    return data;
}

/**
 * Get baggage options from an active transaction
 */
export async function getBaggageOptions(
    sessionId: string
): Promise<{ success: boolean; baggageOptions?: any[] }> {
    const response = await fetch(`${API_BASE}/baggage-options`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId })
    });

    const data = await response.json();

    if (!data.success) {
        throw new Error(data.error || 'Failed to get baggage options');
    }

    return data;
}

/**
 * Update baggage selections for an active transaction
 */
export async function updateBaggage(
    sessionId: string,
    selections: { offerId: string; passengerId: string }[]
): Promise<{ success: boolean; baggageDelta: number; currency: string }> {
    const response = await fetch(`${API_BASE}/update-baggage`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId, selections })
    });

    const data = await response.json();

    if (!data.success) {
        throw new Error(data.error || 'Failed to update baggage');
    }

    return data;
}

/**
 * Set passenger and contact information for the transaction
 */
export async function setPassengers(
    sessionId: string,
    passengers: Passenger[],
    contact: Contact,
    reservationNote: string = ''
): Promise<{ success: boolean }> {
    const response = await fetch(`${API_BASE}/passengers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId, passengers, contact, reservationNote })
    });

    const data = await response.json();

    if (!data.success) {
        throw new Error(data.error || 'Failed to set passenger information');
    }

    return data;
}

/**
 * Get available payment options for the transaction
 */
export async function getPaymentOptions(
    sessionId: string
): Promise<PaymentOption[]> {
    const response = await fetch(`${API_BASE}/payment-options`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId })
    });

    const data = await response.json();

    if (!data.success) {
        throw new Error(data.error || 'Failed to get payment options');
    }

    return data.paymentOptions || [];
}

/**
 * Confirm the booking (commit transaction)
 */
export async function confirmBooking(
    sessionId: string,
    paymentOption?: string,
    paymentTransactionId?: string
): Promise<BookingConfirmation> {
    const response = await fetch(`${API_BASE}/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId, paymentOption, paymentTransactionId })
    });

    const data = await response.json();

    if (!data.success) {
        throw new Error(data.error || 'Failed to confirm booking');
    }

    return {
        success: true,
        reservationNumber: data.reservationNumber,
        bookingId: data.bookingId,
        message: data.message || 'Booking confirmed successfully'
    };
}

/**
 * Get current transaction details and state
 */
export async function getTransactionDetails(
    sessionId: string
): Promise<TransactionDetails> {
    const response = await fetch(`${API_BASE}/${sessionId}/transaction-details`);

    const data = await response.json();

    if (!data.success) {
        throw new Error(data.error || 'Failed to get transaction details');
    }

    return data;
}

/**
 * Get booking status by session ID or reservation number
 */
export async function getBookingStatus(
    id: string
): Promise<{ type: string; status: string; reservationNumber?: string }> {
    const response = await fetch(`${API_BASE}/${id}/status`);

    const data = await response.json();

    if (!data.success) {
        throw new Error(data.error || 'Booking not found');
    }

    return data;
}

/**
 * Get reservation detail from Tourvisio
 */
export async function getReservationDetail(
    reservationNumber: string
): Promise<unknown> {
    const response = await fetch(`${API_BASE}/${reservationNumber}/detail`);

    const data = await response.json();

    if (!data.success) {
        throw new Error(data.error || 'Failed to get reservation detail');
    }

    return data.reservation;
}

// ==========================================
// PAYMENT FLOW
// ==========================================

export interface PaymentRequest {
    sessionId: string;
    paymentOption: number;
    successUrl: string;
    errorUrl: string;
    paymentTypeId?: string;
    installment?: number;
    cardInfo?: {
        cardNo: string;
        expiryMonth: string;
        expiryYear: string;
        cvvNo: string;
        cardHolderFirstName: string;
        cardHolderLastName: string;
    };
}

export interface PaymentResponse {
    paymentTransactionId: string;
    paymentTransactionType: number; // 1=OpenModal, 2=PostForm, 3=PostUrl, 5=Redirect, 6=RenderHtml
    postUrl?: string;
    html?: string;
    scriptUrls?: string[];
    responseType?: number;
}

/**
 * Get payment option details (banks, cards, installments)
 */
export async function getPaymentOptionDetail(
    sessionId: string,
    paymentOption: number
): Promise<unknown> {
    const response = await fetch(`${API_BASE}/payment-option-detail`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId, paymentOption })
    });

    const data = await response.json();

    if (!data.success) {
        throw new Error(data.error || 'Failed to get payment option detail');
    }

    return data.options || [];
}

/**
 * Begin payment transaction
 */
export async function beginPaymentTransaction(
    request: PaymentRequest & { culture?: string }
): Promise<PaymentResponse> {
    const response = await fetch(`${API_BASE}/begin-payment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(request)
    });

    const data = await response.json();

    if (!data.success) {
        throw new Error(data.error || 'Failed to begin payment');
    }

    return data;
}

/**
 * Get transaction status (for async payment flows)
 */
export async function getTransactionStatus(
    sessionId: string
): Promise<{ processStatus: number; status: number; transactionType: number }> {
    const response = await fetch(`${API_BASE}/${sessionId}/transaction-status`);

    const data = await response.json();

    if (!data.success) {
        throw new Error(data.error || 'Failed to get transaction status');
    }

    return data;
}

/**
 * Check payment status (for async payment flows)
 */
export async function checkPaymentStatus(
    paymentTransactionId: string
): Promise<{ status: number; paymentTransactionId: string }> {
    const response = await fetch(`${API_BASE}/check-payment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ paymentTransactionId })
    });

    const data = await response.json();

    if (!data.success) {
        throw new Error(data.error || 'Failed to check payment status');
    }

    return data;
}

// ==========================================
// POST-PAYMENT FINALIZATION
// ==========================================

export interface FinalizeAfterPaymentResponse {
    success: boolean;
    status: 'confirmed' | 'processing' | 'failed' | 'payment_received';
    processStatus?: number;
    message?: string;
    reservation?: {
        reservationNumber: string;
        hotelName: string;
        checkIn: string;
        checkOut: string;
        roomType?: string;
        boardType?: string;
        nights?: number;
        totalPrice: number;
        currency: string;
        status: string;
        bookingId?: string;
        contactEmail?: string;
        passengers?: Array<{
            type: string;
            firstName: string;
            lastName: string;
        }>;
    };
    error?: string;
}

/**
 * Finalize booking after payment gateway redirect.
 * Calls backend which verifies payment, saves to MongoDB, sends email.
 */
export async function finalizeAfterPayment(
    transactionId: string,
    bookingContext?: Record<string, unknown>
): Promise<FinalizeAfterPaymentResponse> {
    const response = await fetch(`${API_BASE}/finalize-after-payment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ transactionId, bookingContext })
    });

    const data = await response.json();

    if (!response.ok && !data.success) {
        return {
            success: false,
            status: 'failed',
            error: data.error || 'Failed to finalize booking',
            processStatus: data.processStatus
        };
    }

    return data;
}

// ==========================================
// CANCELLATION
// ==========================================

export interface CancellationPenalty {
    reason: { id: string; name: string; comment?: string };
    services: Array<{
        id: string;
        name: string;
        price: { amount: number; currency: string };
        isCancelable: boolean;
    }>;
    price: { amount: number; currency: string };
    isCancelable: boolean;
}

/**
 * Get cancellation penalty for a reservation
 */
export async function getCancellationPenalty(
    reservationNumber: string
): Promise<CancellationPenalty[]> {
    const response = await fetch(`${API_BASE}/${reservationNumber}/cancellation-penalty`);

    const data = await response.json();

    if (!data.success) {
        throw new Error(data.error || 'Failed to get cancellation penalty');
    }

    return data.cancelPenalties || [];
}

/**
 * Cancel reservation
 */
export async function cancelReservation(
    reservationNumber: string,
    reasonId: string,
    serviceIds?: string[]
): Promise<{ reservationStatus: number }> {
    const response = await fetch(`${API_BASE}/${reservationNumber}/cancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reasonId, serviceIds })
    });

    const data = await response.json();

    if (!data.success) {
        throw new Error(data.error || 'Failed to cancel reservation');
    }

    return data;
}

// ==========================================
// UTILITY FUNCTIONS
// ==========================================

/**
 * Format time remaining for display
 */
export function formatTimeRemaining(ms: number): string {
    if (ms <= 0) return 'Expired';

    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);

    if (minutes > 0) {
        return `${minutes}m ${seconds}s`;
    }
    return `${seconds}s`;
}

/**
 * Check if transaction is about to expire (less than 2 minutes)
 */
export function isAboutToExpire(ms: number): boolean {
    return ms > 0 && ms < 2 * 60 * 1000;
}

// ==========================================
// BOOKING SERVICE OBJECT (Alternative API)
// ==========================================

export const bookingService = {
    // Transaction management
    startBooking,
    addServices,
    removeServices,
    setPassengers,
    getTransactionDetails,
    getBaggageOptions,
    updateBaggage,

    // Payment flow
    getPaymentOptions,
    getPaymentOptionDetail,
    beginPaymentTransaction,
    getTransactionStatus,
    checkPaymentStatus,

    // Booking completion
    confirmBooking,
    getBookingStatus,
    getReservationDetail,
    finalizeAfterPayment,

    // Cancellation
    getCancellationPenalty,
    cancelReservation,

    // Utilities
    formatTimeRemaining,
    isAboutToExpire
};

export default bookingService;
